import {Box, MenuItem, TextField, Typography} from '@mui/material';
import {SearchableAgentView, useCustomGroupsAndAgents, useMidsContainer} from '@ozark/common';
import {MidSelect, PanelItem, Title} from '@ozark/common/components';
import {VolumeFilterTypes} from '@ozark/functions/src/functions/express/private/types/Reports';
import {orderBy as orderByLodash} from 'lodash';
import {useEffect, useState} from 'react';
import {useUserInfo} from '../../hooks/useUserInfo';
import {FilterPeriodSelect, getFilterPeriod, VolumeFilter} from '../FilterPeriodSelect';
import {ApplicationAnalyticsPage} from './ApplicationAnalytics';
import {MerchantAnalyticsSection} from './MerchantAnalytics';
import TransactionsSummary from './TransactionsSummary';

const DEFAULT_VALUE = '0';

interface Props {
  agents: SearchableAgentView[] | undefined;
}

export const DashboardPage = ({agents}: Props) => {
  const {isMerchant, isErpUser, isAgentAdmin, userGroupId} = useUserInfo();
  const [selectedFilter, setSelectedFilter] = useState<VolumeFilter>(
    getFilterPeriod(VolumeFilterTypes.MTD)
  );
  const {mids, handleSelectMid, selectedMid} = useMidsContainer();
  const {groups, onGroupChange, selectedGroup, filteredAgents, onAgentChange, selectedAgent} =
    useCustomGroupsAndAgents(agents);
  useEffect(() => {
    if (isErpUser || !userGroupId || !isAgentAdmin) {
      return;
    }
    onGroupChange(userGroupId);
  }, [isErpUser, userGroupId, isAgentAdmin]);

  const getBreadcrumbs = () => [<Typography variant="body1">Dashboard</Typography>];

  const handleGroupChange = (event: any) => onGroupChange(event.target.value);
  const handleAgentChange = (event: any) => onAgentChange(event.target.value);

  return (
    <Box>
      <Title breadcrumbs={getBreadcrumbs()}>
        {isErpUser && (
          <PanelItem dividerPosition="end" sx={{minWidth: 200}}>
            <TextField
              value={selectedGroup ?? DEFAULT_VALUE}
              onChange={handleGroupChange}
              label="Filter by Group"
              sx={{width: '100%'}}
              variant="standard"
              InputProps={{
                sx: {
                  backgroundColor: 'transparent !important',
                },
                disableUnderline: true,
              }}
              select
            >
              <MenuItem value="0">All</MenuItem>
              {(groups.data ?? []).sortAndMap(
                group => (
                  <MenuItem key={group.id} value={group.id}>
                    {group.name}
                  </MenuItem>
                ),
                group => group.name
              )}
            </TextField>
          </PanelItem>
        )}
        {(isAgentAdmin || isErpUser) && (
          <PanelItem dividerPosition="end" sx={{minWidth: 200}}>
            <TextField
              value={selectedAgent ?? DEFAULT_VALUE}
              onChange={handleAgentChange}
              label="Filter by Agent"
              sx={{width: '100%'}}
              variant="standard"
              InputProps={{
                sx: {
                  backgroundColor: 'transparent !important',
                },
                disableUnderline: true,
              }}
              select
            >
              <MenuItem value="0">All</MenuItem>
              {orderByLodash(filteredAgents ?? [], ['firstName', 'lastName']).map(agent => (
                <MenuItem key={agent.id} value={agent.id}>
                  {agent.firstName} {agent.lastName}
                </MenuItem>
              ))}
            </TextField>
          </PanelItem>
        )}
        <PanelItem dividerPosition="end">
          <FilterPeriodSelect
            onSelect={setSelectedFilter}
            selected={selectedFilter}
            visibleVolumeFilterTypes={[
              VolumeFilterTypes.OneWeek,
              VolumeFilterTypes.FourWeeks,
              VolumeFilterTypes.OneYear,
              VolumeFilterTypes.MTD,
              VolumeFilterTypes.QTD,
              VolumeFilterTypes.YTD,
              VolumeFilterTypes.All,
              VolumeFilterTypes.Custom,
            ]}
          />
        </PanelItem>
        <Box>
          <MidSelect mids={mids} handleSelectMid={handleSelectMid} selectedMid={selectedMid} />
        </Box>
      </Title>
      <Box pr={2}>
        {!isMerchant && (
          <Box mt={5} mb={7}>
            <ApplicationAnalyticsPage
              filters={selectedFilter}
              selectedGroupId={isErpUser ? selectedGroup : null}
              selectedAgentId={selectedAgent}
            />
          </Box>
        )}
        <Box mt={5}>
          <MerchantAnalyticsSection
            filters={selectedFilter}
            selectedGroupId={isErpUser ? selectedGroup : null}
            selectedAgentId={isErpUser || isAgentAdmin ? selectedAgent : null}
          />
        </Box>
        {isMerchant && (
          <Box mt={5}>
            <TransactionsSummary filters={selectedFilter} />
          </Box>
        )}
      </Box>
    </Box>
  );
};
